import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import { useAppContext } from '@contexts/AppContext';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { article_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';
import { getArticleImgSrc } from '@utils/images';
import { UtmParams, articlePathWithParams } from '@utils/queryStringHelper';

import IconProTagThumbnail from '../Images/Rebranding/IconProTagThumbnail';

interface ArticleImageProps {
  className?: string;
  id: number;
  isMobileFullWidth?: boolean;
  isPro: boolean;
  picture: string;
  sizes?: string;
  skipOptimization?: boolean;
  slug: string;
  target?: string;
  title: string;
  offerCode?: string;
  utmParams?: UtmParams;
  newsletterSlug?: string;
}

const ArticleImage = ({
  className,
  id,
  isMobileFullWidth,
  isPro,
  picture,
  sizes,
  skipOptimization = false,
  slug,
  target,
  title,
  offerCode,
  utmParams,
  newsletterSlug,
}: ArticleImageProps) => {
  const { env } = useAppContext();
  const { trackEvent } = useEventTracking();
  const { variantId } = useBlueconicContext();
  const hydrated = useServerSideRendering();
  const [articlePath, setArticlePath] = useState(article_path(slug));

  useEffect(() => {
    if (!hydrated) return;
    setArticlePath(
      articlePathWithParams({
        slug: slug,
        offer: offerCode,
        utm: utmParams,
        newsletterSlug: newsletterSlug,
      })
    );
  }, [hydrated, slug, offerCode, utmParams, newsletterSlug]);

  const clickTargetCapture = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.ArticleImage,
      })
    );
    if (variantId) {
      captureBlueconicClickEvent(variantId);
    }
  };
  return (
    <a
      href={articlePath}
      onClickCapture={clickTargetCapture}
      className={clsx(
        'relative block flex-shrink-0',
        {
          'max-sm:h-[calc(100vw*9/16)]': !isMobileFullWidth,
          '-mx-16 h-[calc(100vw*9/16)] sm:mx-0': isMobileFullWidth,
        },
        className
      )}
      target={target}
    >
      {isPro && <IconProTagThumbnail className="absolute right-0 top-0" />}
      <LazyImgixImage
        alt={title}
        className="article-image aspect-[16/9] h-auto w-full object-cover"
        src={getArticleImgSrc(id, picture, env)}
        skipOptimization={skipOptimization}
        sizes={sizes}
        wrapperClassName="!flex"
      />
    </a>
  );
};

export default ArticleImage;
